// 最後の一撃・一突きの版 (DEV ?lunaExec=pierce・2026-10-30 作者と決めた設計と5コマ) の光。
//   設計 = docs/research/2026-10-30-luna-finisher-design-talk.md・5コマ = docs/research/2026-10-30-luna-finisher-keyframes-v2.md。
//   作者の答え (2026-10-30・5コマ)「色は常夜燈の色にして」「(刺さったレイピアの名残に見えたら消します。) 消してください」。
//   コードで描いたと分かる線・輪・星型・切れ目は使わない (作者が何度も嫌った)。軌跡は筆先の軌道のような短い1本 (両端が細る)。
//   粒は Elden Ring の撃破のように、針の先ほどの光が体の上から先にふわっと昇り、まばらになって消える (ほこりっぽくしない・灰色の煙は出さない)。
//   時刻はすべて当たりからの ms (sinceHitMs = aliceClockMs - ALICE.hitMs)。同じ時刻なら同じ絵 (撮影・比較で揺れない)。
//   体の芯から光がこぼれる所 (ボスの絵に重ねる) は render.ts の drawPierceBossBody が描く。ここは体の外の光だけ。
import { ALICE, PIERCE } from './luna_exec_swap'
import { LAMP } from './luna_lamp_leak_fx'
import { drawLampMote, lampMoteOld } from './lamp_mote'

type Pt = { x: number; y: number }
type Rgb = readonly [number, number, number]

function rnd(i: number, seed: number): number {
  const s = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453
  return s - Math.floor(s)
}
const clamp01 = (u: number): number => Math.max(0, Math.min(1, u))
const smooth = (u: number): number => { const t = clamp01(u); return t * t * (3 - 2 * t) }
const rgba = (c: Rgb, a: number): string => `rgba(${c[0]},${c[1]},${c[2]},${clamp01(a).toFixed(3)})`

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, c: Rgb, a: number): void {
  if (a <= 0.005 || r <= 0.5) return
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, rgba(c, a)); g.addColorStop(0.3, rgba(c, a * 0.42)); g.addColorStop(1, rgba(c, 0))
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
}

/** 背景を沈める濃さ (0〜PIERCE.dimAlpha)。 */
export function pierceBgDark(sinceHitMs: number): number {
  const inn = smooth((sinceHitMs - PIERCE.dimInFromMs) / PIERCE.dimInMs)
  const out = smooth((sinceHitMs - PIERCE.dimOutFromMs) / PIERCE.dimOutMs)
  return PIERCE.dimAlpha * inn * (1 - out)
}
/** ボスの体の残り (1 → 0)。全体を薄めるのでなく、render.ts が上から欠けさせる (粒と同じ順)。 */
export function pierceBodyAlpha(sinceHitMs: number): number {
  return 1 - clamp01((sinceHitMs - PIERCE.bodyFadeFromMs) / PIERCE.bodyFadeMs)
}
/** 体の欠け: 欠ける所の閾値 (0〜1)。yn = 体の丈の中の高さ (0 = 頭)。粒の生まれる時刻と同じ順 (pierceParticleBornMs)。 */
export function pierceCrumbleThreshold(yn: number, h: number): number {
  return 0.7 * yn + 0.3 * h
}
/** 刺した瞬間のボスの揺れ (この後は止まる)。 */
export function pierceBossShake(sinceHitMs: number): { x: number; y: number } {
  if (sinceHitMs < 0 || sinceHitMs > PIERCE.hitShakeMs) return { x: 0, y: 0 }
  const d = 1 - sinceHitMs / PIERCE.hitShakeMs
  const amp = PIERCE.hitShakePx * d * d
  return { x: Math.sin(sinceHitMs * 0.11) * amp, y: Math.cos(sinceHitMs * 0.13) * amp * 0.4 }
}
/** 体の芯からこぼれる光。amount = 明るさ (0〜1)・reach = 傷から光が届く広さ (0〜1・体の丈に対して)。 */
export function pierceSpill(sinceHitMs: number): { amount: number; reach: number } {
  const u = (sinceHitMs - PIERCE.spillFromMs) / PIERCE.spillMs
  if (u <= 0) return { amount: 0, reach: 0 }
  const reach = smooth(u)
  // 開ききった後は体と一緒に消える (光の量 = 体の残り)
  const amount = smooth(u * 1.6) * (0.5 + 0.5 * pierceBodyAlpha(sinceHitMs))
  return { amount, reach }
}

/** 体の形の点 (render.ts がボスの絵から一度だけ拾う・世界の座標)。 */
export interface PierceSilhouette { pts: readonly Pt[]; top: number; bottom: number; cx: number; halfW: number }

export interface PierceFxInput {
  sinceHitMs: number
  /** 刺した一点 (ボスの胸)。 */
  wound: Pt
  /** 主人公の剣先の x (駆け抜けのずれを足した後)。 */
  tipX: number
  facing: 1 | -1
  /** ボスの大きさの倍率 (人の背丈 = 1)。 */
  scale: number
  silhouette: PierceSilhouette | null
}

/** 世界の層 (カメラの平行移動の内側) に、キャラの上で描く。 */
export function drawPierceFx(ctx: CanvasRenderingContext2D, f: PierceFxInput): void {
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  drawTrail(ctx, f)
  drawPoint(ctx, f)
  drawRise(ctx, f)
  ctx.restore()
}

// 斬った跡: 刺した胸に残る切り傷の形 (作者 10-30「線じゃなくて、斬った跡だと分かるように」・10-31「短すぎて見えない・もっと切り傷みたいに」)。
//   体を斜めに横切る細長い三日月の切れ込み (両端が鋭く尖り、中ほどだけ口が開く)。開いた口の奥ほど白く、縁は常夜燈の青へ沈む。
//   刺した瞬間に頭から尾へ一気に走り、少し残ってから尾から閉じて消える。
function drawTrail(ctx: CanvasRenderingContext2D, f: PierceFxInput): void {
  const t = f.sinceHitMs
  if (t < -40) return
  const fade = clamp01((t - PIERCE.trailFadeFromMs) / PIERCE.trailFadeMs)
  if (fade >= 1) return
  const s = f.scale, dir = f.facing
  const L = PIERCE.trailLen * s
  const ang = PIERCE.trailAngle * dir
  const cos = Math.cos(ang), sin = Math.sin(ang)
  const P = (u: number, v: number): Pt => ({ x: f.wound.x + dir * u * cos - v * sin, y: f.wound.y + dir * u * sin + v * cos })
  // 走り: 刺す 40ms 前から当たりの 50ms 後までに頭から尾まで伸びる。閉じる時は尾から縮み、口も細る。
  const draw = clamp01((t + 40) / 90)
  const open = (0.55 + 0.45 * smooth(draw)) * (1 - fade * 0.8)
  const head = draw, tail = fade * 0.85 * head
  if (head - tail < 0.02) return
  const N = 32
  const outer: Pt[] = [], inner: Pt[] = []
  for (let i = 0; i <= N; i++) {
    const k = tail + (head - tail) * (i / N)
    const u = (0.5 - k) * 2 * L   // 頭 (k=1) が振り抜いた先 = 前・上
    const bulge = Math.sin(clamp01((k - tail) / Math.max(0.001, head - tail)) * Math.PI) ** 0.8
    // 外の弧は大きく反り、内の弧はほぼまっすぐ = 片側だけ膨らむ切れ込み
    const bow = (1 - (u / L) ** 2) * 3.2 * s
    outer.push(P(u, -bow - bulge * 4.2 * s * open))
    inner.push(P(u, -bow + bulge * 1.1 * s * open))
  }
  const a = 1 - fade * fade
  const fillShape = (grow: number, c: Rgb, alpha: number): void => {
    ctx.fillStyle = rgba(c, alpha)
    ctx.beginPath()
    outer.forEach((p, i) => { if (i === 0) ctx.moveTo(p.x, p.y - grow); else ctx.lineTo(p.x, p.y - grow) })
    for (let i = inner.length - 1; i >= 0; i--) ctx.lineTo(inner[i].x, inner[i].y + grow)
    ctx.closePath(); ctx.fill()
  }
  fillShape(4.2 * s, LAMP.deep, 0.16 * a)
  fillShape(1.8 * s, LAMP.mid, 0.34 * a)
  fillShape(0, LAMP.core, 0.95 * a)
  // 口の奥の白: 開いた所だけ細く明るい芯
  const mid = Math.floor(N / 2)
  const m = { x: (outer[mid].x + inner[mid].x) / 2, y: (outer[mid].y + inner[mid].y) / 2 }
  glow(ctx, m.x, m.y, 10 * s * open, [255, 255, 255], 0.35 * a * open)
}

/** 中心の光の強さ: ピカッと灯って少し落ち着き、体が消えるのに合わせて消える (粒になって上へ散る)。 */
export function piercePointAlpha(sinceHitMs: number): number {
  const t = sinceHitMs - PIERCE.pointFromMs
  if (t <= 0) return 0
  const pop = smooth(t / PIERCE.pointGrowMs)
  // 灯った直後の閃き (1.7 倍) → 0.35 秒で落ち着く
  const flash = 1 + 0.7 * Math.max(0, 1 - (t - PIERCE.pointGrowMs) / 350) * pop
  const scatter = smooth((sinceHitMs - PIERCE.coreScatterFromMs) / PIERCE.coreScatterMs)
  return pop * flash * (1 - scatter)
}

// 刺した一点: 丸くやわらかい光だけ (光の筋・輪は出さない)。灯った瞬間に弾け (大きく膨らむ光と、外へ飛んで止まる火花)、
//   体が消えるにつれて細かい粒になって上へ散る (10-31 作者「中心の光が小さい蛍の光みたい・破裂感が欲しい」。散り方はそのまま)。
function drawPoint(ctx: CanvasRenderingContext2D, f: PierceFxInput): void {
  const t = f.sinceHitMs
  const s = f.scale
  const a = piercePointAlpha(t)
  const tp = t - PIERCE.pointFromMs
  // 弾ける: 一瞬で大きく膨らみ (50ms)、その後 0.32 秒で引く。
  if (tp > 0 && tp < PIERCE.pointGrowMs + PIERCE.burstMs) {
    const grow = smooth(tp / PIERCE.pointGrowMs)
    const fall = 1 - clamp01((tp - PIERCE.pointGrowMs) / PIERCE.burstMs)
    const pop = grow * fall * fall
    const swell = 0.55 + 0.45 * grow + 0.35 * (1 - fall)   // 引きながら少し広がり続ける
    glow(ctx, f.wound.x, f.wound.y, 150 * s * swell, LAMP.deep, 0.32 * pop)
    glow(ctx, f.wound.x, f.wound.y, 78 * s * swell, LAMP.mid, 0.6 * pop)
    glow(ctx, f.wound.x, f.wound.y, 30 * s * (0.8 + 0.2 * grow), LAMP.core, pop)
    glow(ctx, f.wound.x, f.wound.y, 14 * s, [255, 255, 255], 0.9 * pop)
  }
  // 火花: 弾けた瞬間に外へ速く飛び、すぐ止まってふっと消える (針の先ほどの点・線にはしない)。
  if (tp > 0) {
    const [lmin, lmax] = PIERCE.burstSparkLifeMs
    for (let i = 0; i < PIERCE.burstSparkCount; i++) {
      const life = lmin + (lmax - lmin) * rnd(i, 61)
      const age = tp - 10 * rnd(i, 62)
      if (age < 0 || age > life) continue
      const k = age / life
      const th = rnd(i, 63) * Math.PI * 2
      const reach = (40 + 70 * rnd(i, 64)) * s
      const d = reach * (1 - (1 - k) ** 3)   // 速く出て、止まる
      const x = f.wound.x + Math.cos(th) * d
      const y = f.wound.y + Math.sin(th) * d * 0.8 - 14 * s * k * k
      const al = (1 - k) ** 1.5
      ctx.fillStyle = rgba(rnd(i, 65) < 0.6 ? LAMP.core : LAMP.mid, al)
      ctx.beginPath(); ctx.arc(x, y, (0.7 + 0.8 * rnd(i, 66)) * Math.min(1.3, s) * (1 - 0.4 * k), 0, Math.PI * 2); ctx.fill()
    }
  }
  if (a > 0.005) {
    const spill = pierceSpill(t)
    glow(ctx, f.wound.x, f.wound.y, (26 + 40 * spill.reach) * s * Math.min(1.3, a), LAMP.mid, Math.min(0.75, 0.46 * a))
    glow(ctx, f.wound.x, f.wound.y, 12 * s * Math.min(1.4, a), LAMP.core, Math.min(1, 0.9 * a))
    ctx.fillStyle = rgba(LAMP.core, Math.min(1, a))
    ctx.beginPath(); ctx.arc(f.wound.x, f.wound.y, 2.6 * s * Math.min(1.3, a), 0, Math.PI * 2); ctx.fill()
  }
  // 中心の光がほどけた細かい粒: 中心から生まれ、上へ速めに昇りながら細く広がって消える
  const sc0 = PIERCE.coreScatterFromMs
  if (t < sc0) return
  for (let i = 0; i < PIERCE.coreParticleCount; i++) {
    const born = sc0 + PIERCE.coreScatterMs * Math.sqrt(rnd(i, 51))
    const life = 550 + 450 * rnd(i, 52)
    const age = t - born
    if (age < 0 || age > life) continue
    const k = age / life, sec = age / 1000
    const r0 = 6 * s * Math.sqrt(rnd(i, 53))
    const th = rnd(i, 54) * Math.PI * 2
    const v = (40 + 50 * rnd(i, 55)) * s
    const x = f.wound.x + Math.cos(th) * r0 + (rnd(i, 56) - 0.5) * 40 * s * sec
    const y = f.wound.y + Math.sin(th) * r0 * 0.7 - v * sec - v * 0.4 * sec * sec
    const al = clamp01(age / 80) * (1 - k * k) * (0.7 + 0.3 * rnd(i, 57))
    ctx.fillStyle = rgba(rnd(i, 58) < 0.75 ? LAMP.core : LAMP.mid, al)
    ctx.beginPath(); ctx.arc(x, y, (0.45 + 0.5 * rnd(i, 59)) * Math.min(1.25, s), 0, Math.PI * 2); ctx.fill()
  }
}

/** 粒 i の生まれる時刻 (当たりからの ms)。体の上の方ほど先に昇り始める。 */
export function pierceParticleBornMs(i: number, yn: number): number {
  return PIERCE.riseFromMs + PIERCE.riseSpanMs * pierceCrumbleThreshold(yn, rnd(i, 41))
}

// 昇る粒: 体の形の点から生まれ、ふわっと上へ昇りながら扇に広がり、一粒ずつ消える (薄まるより、まばらになって消える)。
function drawRise(ctx: CanvasRenderingContext2D, f: PierceFxInput): void {
  const sil = f.silhouette
  if (!sil || sil.pts.length === 0 || f.sinceHitMs < PIERCE.riseFromMs) return
  const t = f.sinceHitMs
  const [lifeMin, lifeMax] = PIERCE.particleLifeMs
  const span = Math.max(1, sil.bottom - sil.top)
  for (let i = 0; i < PIERCE.particleCount; i++) {
    const p = sil.pts[Math.floor(rnd(i, 31) * sil.pts.length)]
    const yn = clamp01((p.y - sil.top) / span)
    const born = pierceParticleBornMs(i, yn)
    const life = lifeMin + (lifeMax - lifeMin) * rnd(i, 32)
    const age = t - born
    if (age < 0 || age > life) continue
    const k = age / life, sec = age / 1000
    // 上へ: 初めはゆっくり、少しずつ速く (浮かぶ)。横へ: 体の中心から外へ広がる・わずかに揺れる。
    const v = (16 + 34 * rnd(i, 33)) * f.scale
    const y = p.y - v * sec - v * 0.55 * sec * sec
    const side = (p.x - sil.cx) / Math.max(1, sil.halfW)
    const x = p.x + side * (10 + 22 * rnd(i, 34)) * f.scale * sec + Math.sin(sec * (1.6 + rnd(i, 35)) * Math.PI + rnd(i, 36) * 6) * 3.5 * f.scale * sec
    const twinkle = 0.78 + 0.22 * Math.sin(age / 1000 * (7 + 5 * rnd(i, 37)) + rnd(i, 38) * 6)
    // 生まれた瞬間は少し明るく (体の欠けを光で隠す)
    const a = clamp01(age / 60) * (1 - k * k) * twinkle * (1 + 0.6 * Math.max(0, 1 - age / 220))
    const big = rnd(i, 39) < 0.07
    const c = rnd(i, 40) < 0.7 ? LAMP.core : LAMP.mid
    const r = (big ? 1.5 : 0.55 + 0.75 * rnd(i, 42)) * Math.min(1.25, f.scale)
    if (!lampMoteOld()) { drawLampMote(ctx, x, y, Math.min(1, a)); continue }   // 1粒は常夜燈の光の正本 (src/lamp_mote.ts・10-31)
    if (big) glow(ctx, x, y, r * 4, LAMP.mid, 0.35 * a)
    ctx.fillStyle = rgba(c, a)
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
  }
}

/** 当たりからの ms (止め・撃破の時計を含む)。 */
export const pierceSinceHitMs = (aliceClockMs: number): number => aliceClockMs - ALICE.hitMs
